import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useBookingRecord } from "@/app/Bookings/Hooks/useBookingRecord";
import { useTranslations } from "use-intl";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { BookingChargeType, RelocationType } from "@/gql/graphql";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function BookingPaymentCard() {
  const booking = useBookingRecord();

  const t = useTranslations("booking.cards.orderCard");
  const tRelocation = useTranslations("relocation");

  // Gap rentals have no relocation nights, so the hire nights aren't "extra".
  const isGapRental = booking.relocation.type === RelocationType.GapRental;
  const dailyRateLabel = isGapRental
    ? t("hireUnit", { type: booking.hire_unit_type })
    : t("extraHireUnit", { type: booking.hire_unit_type });

  const relocationRate = booking.charges.find(
    (c) => c.type === BookingChargeType.RelocationRate,
  );

  const dailyRate = booking.charges.find(
    (c) => c.type === BookingChargeType.DailyRate,
  );

  const additionalCharges = booking.charges.filter(
    (c) => c.type === BookingChargeType.AdditionalCharge,
  );

  const purchasableExtraCharges = booking.charges.filter(
    (c) => c.type === BookingChargeType.PurchasableExtra && c.quantity > 0,
  );

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6 text-sm">
        <div className="grid gap-3">
          <ul className="grid gap-3">
            <div className="font-semibold">
              Payable To {booking.supplier.name}
            </div>
            {relocationRate && relocationRate.quantity > 0 ? (
              <li>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">
                    Relocation{" "}
                    {tRelocation(`hire_unit_type.${booking.hire_unit_type}`)}s
                  </span>
                  <span>
                    {formatCurrency(
                      relocationRate.supplier_amount_collectable,
                      booking.currency,
                    )}
                  </span>
                </div>
                {relocationRate.quantity > 0 ? (
                  <div className="mt-0.5 text-xs text-gray-400">
                    {relocationRate.quantity} x{" "}
                    {formatCurrency(
                      relocationRate.supplier_unit_price_collectable,
                      booking.currency,
                    )}
                  </div>
                ) : null}
              </li>
            ) : null}
            {dailyRate && dailyRate.quantity > 0 ? (
              <li>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">
                    {dailyRate.platform_amount_collectable > 0 ? (
                      <Popover>
                        <PopoverTrigger
                          type="button"
                          className="inline-flex items-center gap-1 hover:text-gray-600"
                        >
                          {dailyRateLabel}
                          <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                        </PopoverTrigger>
                        <PopoverContent
                          side="bottom"
                          align="start"
                          className="p-3"
                        >
                          <dl className="grid gap-1 text-xs">
                            <div className="flex justify-between gap-6">
                              <dt className="text-gray-500">
                                Imoova commission
                              </dt>
                              <dd>
                                {formatCurrency(
                                  dailyRate.platform_unit_price_collectable,
                                  booking.currency,
                                )}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-6">
                              <dt className="text-gray-500">Supplier net</dt>
                              <dd>
                                {formatCurrency(
                                  dailyRate.supplier_unit_price_collectable,
                                  booking.currency,
                                )}
                              </dd>
                            </div>
                          </dl>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      dailyRateLabel
                    )}
                  </span>
                  <span>
                    {formatCurrency(dailyRate.amount, booking.currency)}
                  </span>
                </div>
                {dailyRate.quantity > 0 ? (
                  <div className="mt-0.5 text-xs text-gray-400">
                    {dailyRate.quantity} x{" "}
                    {formatCurrency(dailyRate.unit_price, booking.currency)}
                  </div>
                ) : null}
              </li>
            ) : null}

            {additionalCharges.map((charge) => (
              <li key={charge.id}>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">
                    {charge.inclusion?.description ?? "Additional charge"}
                  </span>
                  <span>
                    {formatCurrency(
                      charge.supplier_amount_collectable,
                      booking.currency,
                    )}
                  </span>
                </div>
                {charge.quantity > 0 ? (
                  <div className="mt-0.5 text-xs text-gray-400">
                    {charge.quantity} x{" "}
                    {formatCurrency(
                      charge.supplier_unit_price_collectable,
                      booking.currency,
                    )}
                  </div>
                ) : null}
              </li>
            ))}

            {purchasableExtraCharges.map((charge) => (
              <li key={charge.id}>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">
                    {charge.purchasableExtra?.supplierPurchasableExtra.name}
                  </span>
                  <span>
                    {formatCurrency(
                      charge.supplier_amount_collectable,
                      booking.currency,
                    )}
                  </span>
                </div>
                {charge.quantity > 0 ? (
                  <div className="mt-0.5 text-xs text-gray-400">
                    {charge.quantity} x{" "}
                    {formatCurrency(
                      charge.supplier_unit_price_collectable,
                      booking.currency,
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <Separator className="my-2" />
          <ul className="grid gap-3">
            {(dailyRate && dailyRate.platform_amount_collectable > 0) ||
            booking.charge_summary.supplier_refund_amount !== 0 ? (
              <li className="flex items-center justify-between">
                <span className="text-gray-500">{t("subtotal")}</span>
                <span>
                  {formatCurrency(
                    booking.charge_summary.pay_later_subtotal_amount +
                      (dailyRate?.platform_amount_collectable || 0),
                    booking.currency,
                  )}
                </span>
              </li>
            ) : null}
            {dailyRate && dailyRate.platform_amount_collectable > 0 ? (
              <li className="flex items-center justify-between">
                <span className="text-gray-500">
                  <Popover>
                    <PopoverTrigger
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-gray-600"
                    >
                      Imoova commission
                      <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="start" className="p-3">
                      <p className="text-xs">
                        {dailyRate.quantity} x{" "}
                        {formatCurrency(
                          dailyRate.platform_unit_price_collectable,
                          booking.currency,
                        )}{" "}
                        collected by Imoova at booking.
                      </p>
                    </PopoverContent>
                  </Popover>
                </span>
                <span>
                  {formatCurrency(
                    -dailyRate.platform_amount_collectable,
                    booking.currency,
                  )}
                </span>
              </li>
            ) : null}
            {booking.charge_summary.supplier_refund_amount !== 0 ? (
              <li className="flex items-center justify-between">
                <span className="text-gray-500">
                  <Popover>
                    <PopoverTrigger
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-gray-600"
                    >
                      Holding deposit refund
                      <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="start" className="p-3">
                      <p className="text-xs">
                        Collected by Imoova at booking and refunded by the
                        supplier in lieu of payment.
                      </p>
                    </PopoverContent>
                  </Popover>
                </span>
                <span>
                  {formatCurrency(
                    -booking.charge_summary.supplier_refund_amount,
                    booking.currency,
                  )}
                </span>
              </li>
            ) : null}
            <li className="flex items-center justify-between font-semibold">
              <span className="text-gray-500">{t("total")}</span>
              <span>
                {formatCurrency(
                  booking.charge_summary.pay_later_total_amount,
                  booking.currency,
                )}
              </span>
            </li>
          </ul>

          {booking.charge_summary.platform_refund_amount > 0 ? (
            <>
              <Separator className="my-2" />
              <ul>
                <li className="flex items-center justify-between">
                  <span className="text-gray-500">
                    Deposit refund (after hire via Imoova)
                  </span>
                  <span>
                    (
                    {formatCurrency(
                      booking.charge_summary.platform_refund_amount,
                      booking.currency,
                    )}
                    )
                  </span>
                </li>
              </ul>
            </>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
